import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheckCircle,
  faShieldAlt,
  faCog,
  faLayerGroup,
  faSlidersH,
  faSync,
  faRobot,
  faRocket,
} from '@fortawesome/free-solid-svg-icons';
import { MockGridCard, MetricRow, MetricSection } from '../shared/MockGridCard';

const WFABridgeCards = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Parameter Stability */}
    <MockGridCard title="Parameter Stability" icon={faSlidersH} iconColor="text-purple-400">
      <div className="flex flex-col h-full text-sm">
        <div className="mb-3">
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-lime-200/10">
            <FontAwesomeIcon icon={faCheckCircle} className="text-lime-200 text-xs" />
            <span className="text-xs font-semibold text-lime-200">Stable</span>
          </div>
          <p className="text-[11px] text-gray-400 leading-tight mt-2">
            Parameters remain consistent across all WFA windows.
          </p>
        </div>
        <div className="flex-1 space-y-1">
          <MetricRow label="Avg Drift" value="3.2%" />
          <MetricRow label="Stable Params" value="92%" />
          <MetricRow label="Window Consistency" value="87%" />
        </div>
        <div className="mt-auto pt-2 border-t border-gray-700">
          <p className="text-[10px] text-gray-400 italic">Parameter stability supports fixed-parameter deployment.</p>
        </div>
      </div>
    </MockGridCard>

    {/* Re-Opt Burden */}
    <MockGridCard title="Re-Optimization Burden" icon={faCog} iconColor="text-amber-400">
      <div className="flex flex-col h-full">
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-lime-300/10 mb-3 self-start">
          <span className="text-xs font-semibold text-lime-200">Low Burden</span>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400"><FontAwesomeIcon icon={faSync} className="mr-1" />Change Frequency</span>
            <span className="text-xs font-medium text-gray-300">12%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400"><FontAwesomeIcon icon={faRobot} className="mr-1" />Automation Potential</span>
            <span className="text-xs font-medium text-gray-300">88%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400"><FontAwesomeIcon icon={faLayerGroup} className="mr-1" />Regime Impact</span>
            <span className="text-xs font-medium text-lime-300">None</span>
          </div>
        </div>
        <div className="mt-3 pt-2 border-t border-gray-700 flex items-center justify-between">
          <div>
            <div className="text-[10px] text-gray-400">Monitor</div>
            <div className="text-sm font-semibold text-lime-300">Quarterly</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-gray-400">Burden Score</div>
            <div className="text-sm font-semibold text-white">18%</div>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-700">
          <p className="text-[10px] text-gray-400 italic">Strategy adapts well to changing conditions with minimal intervention.</p>
        </div>
      </div>
    </MockGridCard>

    {/* WFA Validation */}
    <MockGridCard title="WFA Validation Status" icon={faShieldAlt} iconColor="text-blue-400">
      <div className="flex flex-col h-full text-sm">
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1">
            <FontAwesomeIcon icon={faCheckCircle} className="text-lime-200" />
            <span className="text-lg font-bold text-lime-200 uppercase tracking-wide">Pass</span>
          </div>
          <p className="text-[11px] text-gray-400 leading-tight">Forward performance retained with acceptable stability.</p>
        </div>
        <div className="mb-2">
          <MetricSection label="Validation Quality" />
          <MetricRow label="WFA Efficiency" value="0.85" />
          <MetricRow label="Performance Degradation" value="21%" />
        </div>
        <div className="mb-2">
          <MetricSection label="Outcome Context" />
          <MetricRow label="OOS Sharpe" value="1.42" muted />
          <MetricRow label="Success Rate" value="75%" muted />
        </div>
        <div className="mt-auto pt-2 border-t border-gray-700">
          <p className="text-[10px] text-gray-400 italic">Ready for live deployment via Adaptive Flow.</p>
        </div>
      </div>
    </MockGridCard>

    {/* Live Readiness */}
    <MockGridCard title="Live Readiness" icon={faRocket} iconColor="text-sky-400">
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-lime-900/30 border border-lime-500/30 mb-3">
          <FontAwesomeIcon icon={faCheckCircle} className="text-lg text-lime-300" />
          <div className="flex-1">
            <span className="text-sm font-bold text-lime-300">Eligible</span>
            <span className="text-xs text-gray-400 ml-2">86% confidence</span>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mb-2 italic">
          Deployment suitability based on forward robustness, risk behaviour, and operational burden.
        </p>
        <div className="flex-1 space-y-1">
          {[
            'Forward performance validated across rolling windows',
            'Drawdown behaviour contained within bounds',
            'Parameter stability supports fixed deployment',
            'Low re-optimization burden confirmed',
          ].map((factor) => (
            <div key={factor} className="flex items-start gap-1.5 text-[10px] text-gray-400">
              <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 text-lime-500" />
              <span>{factor}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-2 border-t border-gray-700">
          <div className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-white text-xs font-medium" style={{ backgroundColor: '#aa48ff' }}>
            Enable Adaptive Flow
            <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
          </div>
        </div>
      </div>
    </MockGridCard>
  </div>
);

export default WFABridgeCards;
